import { css } from "@emotion/react";
import React, { useCallback, useEffect, useState } from "react";
import { useTheme } from "../theme";

export interface DefaultDropAreaInnerProps extends React.ComponentPropsWithoutRef<"div"> {
  accepted?: boolean;
}

const DefaultDropAreaInner = ({
  accepted,
  ...props
}: DefaultDropAreaInnerProps) => {
  const { palette, transitions } = useTheme();
  return (
    <div
      {...props}
      css={[
        css`
          box-sizing: border-box;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: ${palette.background.paper};
          border: 2px dashed ${palette.gray[300]};
          border-radius: 4px;
        `,
        (accepted === undefined || accepted) &&
          css`
            transition: border-color ${transitions.duration.shorter}
              ${transitions.easing.easeIn};
            &[data-dropzone-status="dragover"] {
              background-color: ${palette.intentions.primary.light};
              border-color: ${palette.intentions.primary.main};
            }
          `,
      ]}
    />
  );
};

interface BaseDropAreaProps {
  onDrop: (files: File[]) => void;
  onDragEnter?: never;
  onDragLeave?: never;
  onDragOver?: never;
  accept?: RegExp;
}

export interface DropAreaProps
  extends
    BaseDropAreaProps,
    Omit<React.ComponentPropsWithoutRef<"div">, keyof BaseDropAreaProps> {}

export const DropArea = ({ onDrop, accept, ...props }: DropAreaProps) => {
  const handleCancel = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);
  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.dataTransfer.files.length > 0) {
        onDrop(
          accept
            ? Array.from(e.dataTransfer.files).filter((file) =>
                accept?.test(file.type),
              )
            : Array.from(e.dataTransfer.files),
        );
        e.dataTransfer.clearData();
      }
      delete e.currentTarget.dataset.dropAreaStatus;
    },
    [accept, onDrop],
  );
  const [status, setStatus] = useState("");
  const [accepted, setAccepted] = useState<boolean>(false);
  useEffect(() => {
    const handler = (event: DragEvent) => {
      setAccepted(event.dataTransfer?.types.includes("Files") ?? false);
      setStatus(event.type);
      event.preventDefault();
      event.stopPropagation();
    };
    document.addEventListener("drop", handler);
    document.addEventListener("dragover", handler);
    document.addEventListener("dragleave", handler);
    return () => {
      document.removeEventListener("drop", handler);
      document.removeEventListener("dragover", handler);
      document.removeEventListener("dragleave", handler);
    };
  }, []);
  return (
    <DefaultDropAreaInner
      data-dropzone-status={status}
      {...props}
      accepted={accepted}
      onDragEnter={handleCancel}
      onDragLeave={handleCancel}
      onDragOver={handleCancel}
      onDrop={handleDrop}
    />
  );
};
